import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, EmptyValue, Form, IdLink, Modal, PageHeader, Spinner, formatServiceArea } from '@/components/ui';
import { FleetFilterOptions, Vehicle } from '../../../types/Vehicle';
import vehicleService, { fieldErrorsOf } from '../../../services/vehicleService';
import equipmentFleetService from '../../../services/equipmentFleetService';
import EquipmentSubnav from '../components/EquipmentSubnav';
import { AvailabilityPill, availabilityState } from '../components/EquipmentStatusPill';
import EquipmentFlags from '../components/EquipmentFlags';
import { EquipmentScopeFilters, useEquipmentScope } from '../components/EquipmentFilters';
import { usePermissions } from '../../../hooks/usePermissions';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { EQUIPMENT_PERMISSIONS } from '../utils/equipmentPermissions';
import { formatDate, todayPacific } from '../../../utils/wrapDate';
import { scrollParent } from '../../../utils/scrollParent';

/** "1 open defect", "3 open defects". */
export function openDefectsText(count: number): string {
  return `${count.toLocaleString('en-CA')} open ${count === 1 ? 'defect' : 'defects'}`;
}

/** The unit as the board shows it after a change: the API's full unit over the board's copy (STATE-06). */
export function mergeUpdatedUnit(units: Vehicle[], updated: Vehicle): Vehicle[] {
  // Availability doesn't move a unit, so it keeps the board's foreman area (with its service area)
  // and stays in its group.
  return units.map((unit) =>
    unit.id === updated.id ? { ...unit, ...updated, foremanArea: unit.foremanArea ?? updated.foremanArea } : unit
  );
}

const SCROLL_KEY = 'wrap.equipment.board.scrollY';

function rememberScroll(event: React.MouseEvent<Element>) {
  try {
    sessionStorage.setItem(SCROLL_KEY, String(scrollParent(event.currentTarget).scrollTop));
  } catch {
    /* storage unavailable: the board just opens at the top */
  }
}

function takeRememberedScroll(): number | null {
  try {
    const raw = sessionStorage.getItem(SCROLL_KEY);
    sessionStorage.removeItem(SCROLL_KEY);
    return raw == null ? null : Number(raw);
  } catch {
    return null;
  }
}

type Target = 'InService' | 'OutOfService';

interface ShopCardProps {
  unit: Vehicle;
  canSetAvailability: boolean;
  onSetAvailability: (unit: Vehicle) => void;
}

/** One unit on the board (EQ-17): the unit number opens it, the pill changes its availability. */
export const ShopCard: React.FC<ShopCardProps> = ({ unit, canSetAvailability, onSetAvailability }) => {
  const outOfService = availabilityState(unit) === 'OutOfService';
  const returnDay = formatDate(unit.estimatedReturnToServiceDate ?? null);
  const flags = (unit.flags ?? []).filter((flag) => flag.key !== 'openDefects');
  return (
    <article
      className="tw-flex tw-min-h-[7rem] tw-flex-col tw-gap-2 tw-rounded-lg tw-border tw-border-line-input tw-bg-surface tw-p-4 tw-shadow-card"
      data-testid={`shop-card-${unit.id}`}
    >
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
        <div className="tw-min-w-0">
          <IdLink to={`/equipment/${unit.id}`} className="tw-text-lg" onClick={rememberScroll}>
            {unit.unitNumber}
          </IdLink>
          <div className="tw-text-sm tw-text-content-secondary">
            {unit.description || unit.vehicleClassification || <EmptyValue />}
          </div>
        </div>
        <AvailabilityPill
          vehicle={unit}
          onClick={canSetAvailability ? () => onSetAvailability(unit) : undefined}
        />
      </div>
      {outOfService && (
        <dl className="tw-m-0 tw-grid tw-grid-cols-[auto,1fr] tw-gap-x-2 tw-text-xs">
          <dt className="tw-text-content-muted">Reason</dt>
          <dd className="tw-m-0 tw-text-content">{unit.availabilityReason || <EmptyValue />}</dd>
          <dt className="tw-text-content-muted">Expected Return</dt>
          <dd className="tw-m-0 tw-tabular-nums tw-text-content">{returnDay ?? <EmptyValue />}</dd>
        </dl>
      )}
      <div className="tw-mt-auto tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-xs tw-text-content-muted">
        {unit.openDefectCount ? <span>{openDefectsText(unit.openDefectCount)}</span> : null}
        <EquipmentFlags flags={flags} />
      </div>
    </article>
  );
};

const EquipmentShopBoardPage: React.FC = () => {
  useDocumentTitle('Equipment');
  const { hasPermission } = usePermissions();
  const canSetAvailability = hasPermission(EQUIPMENT_PERMISSIONS.availabilitySet);
  const [options, setOptions] = useState<FleetFilterOptions | null>(null);
  const { scope, setScope, defaultScope } = useEquipmentScope(options);
  const [units, setUnits] = useState<Vehicle[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restoreScroll, setRestoreScroll] = useState<number | null>(() => takeRememberedScroll());

  const [selected, setSelected] = useState<Vehicle | null>(null);
  const [target, setTarget] = useState<Target>('OutOfService');
  const [reason, setReason] = useState('');
  const [estimatedReturn, setEstimatedReturn] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    equipmentFleetService
      .getFilterOptions()
      .then(setOptions)
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load the filters');
        setOptions({ currentStatuses: [], serviceAreas: [], vehicleClassifications: [], currentYards: [], vehicleTypes: [], foremanAreas: [], myForemanAreaIds: [] });
      });
  }, []);

  const scopeKey = scope ? `${scope.serviceArea}|${scope.foremanArea}` : null;
  useEffect(() => {
    if (!scope) return;
    let cancelled = false;
    setLoading(true);
    equipmentFleetService
      .getShopBoard(scope)
      .then((rows) => {
        if (cancelled) return;
        setUnits(rows);
        setError(null);
      })
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : 'Failed to load the Shop Board'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  // Back from a unit: return to where the user was on the board (EQ-17).
  useLayoutEffect(() => {
    if (!loading && restoreScroll != null) {
      scrollParent(rootRef.current).scrollTop = restoreScroll;
      setRestoreScroll(null);
    }
  }, [loading, restoreScroll]);

  const groups = useMemo(() => {
    const map = new Map<string, Vehicle[]>();
    units.forEach((unit) => {
      const area = unit.foremanArea as (Vehicle['foremanArea'] & { serviceArea?: { name: string; code?: string | null } | null }) | undefined;
      const sa = area?.serviceArea ? formatServiceArea(area.serviceArea, true) : null;
      const key = area?.name ? (sa ? `${area.name} (${sa})` : area.name) : 'No Foreman Area';
      const list = map.get(key) || [];
      list.push(unit);
      map.set(key, list);
    });
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [units]);

  const openAvailability = (unit: Vehicle) => {
    setSelected(unit);
    setTarget(availabilityState(unit) === 'InService' ? 'OutOfService' : 'InService');
    setReason('');
    setEstimatedReturn('');
    setFieldErrors({});
  };

  const confirm = async () => {
    if (!selected) return;
    const errors: Record<string, string> = {};
    if (target === 'OutOfService' && !reason.trim()) errors.reason = 'Reason required';
    if (target === 'OutOfService' && estimatedReturn && estimatedReturn < todayPacific()) {
      errors.estimatedReturnToServiceDate = "Estimated return can't be before today";
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setSaving(true);
    try {
      const updated = await vehicleService.setAvailability(selected.id, {
        availability: target,
        reason: target === 'OutOfService' ? reason.trim() : undefined,
        estimatedReturnToServiceDate: target === 'OutOfService' ? estimatedReturn || null : null
      });
      // The card shows its new state where it is; no reload, the scroll stays (STATE-06).
      setUnits((prev) => mergeUpdatedUnit(prev, updated));
      setSelected(null);
    } catch (err) {
      setFieldErrors(fieldErrorsOf(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div ref={rootRef} className="tw-w-full tw-p-4">
      <PageHeader title="Equipment" />
      <EquipmentSubnav />

      {options && scope && (
        <EquipmentScopeFilters
          options={options}
          scope={scope}
          defaultScope={defaultScope}
          onChange={setScope}
          subtitle="Active units by foreman area. Opens on your foreman areas."
        />
      )}

      {error && <Alert variant="danger" className="tw-mb-3">{error}</Alert>}
      {loading && (
        <div className="tw-py-8 tw-text-center">
          <Spinner role="status"><span className="tw-sr-only">Loading...</span></Spinner>
        </div>
      )}

      {!loading && groups.map(([area, areaUnits]) => (
        <section key={area} className="tw-mb-6">
          <h2 className="tw-mb-3 tw-text-lg tw-font-semibold tw-text-content">{area}</h2>
          <div className="tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2 xl:tw-grid-cols-3">
            {areaUnits.map((unit) => (
              <ShopCard
                key={unit.id}
                unit={unit}
                canSetAvailability={canSetAvailability}
                onSetAvailability={openAvailability}
              />
            ))}
          </div>
        </section>
      ))}

      {!loading && scope && groups.length === 0 && (
        <p className="tw-text-sm tw-text-content-muted">No active units in these areas.</p>
      )}

      <Modal show={!!selected} onHide={() => !saving && setSelected(null)} size="sm">
        <Modal.Header closeButton>
          <Modal.Title>
            {selected
              ? `${target === 'OutOfService' ? 'Take Out of Service' : 'Return to Service'} – ${selected.unitNumber}`
              : 'Set Availability'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {target === 'OutOfService' ? (
            <>
              <Form.Label htmlFor="board-reason">Reason <span className="tw-text-danger">*</span></Form.Label>
              <Form.Control
                id="board-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                isInvalid={!!fieldErrors.reason}
                aria-describedby={fieldErrors.reason ? 'board-reason-error' : undefined}
              />
              {fieldErrors.reason && (
                <div id="board-reason-error" className="tw-mt-1 tw-text-sm tw-text-danger">{fieldErrors.reason}</div>
              )}
              <Form.Label htmlFor="board-return" className="tw-mt-3">Estimated Return (Optional)</Form.Label>
              <Form.Control
                id="board-return"
                type="date"
                min={todayPacific()}
                value={estimatedReturn}
                onChange={(e) => setEstimatedReturn(e.target.value)}
                isInvalid={!!fieldErrors.estimatedReturnToServiceDate}
                aria-describedby={fieldErrors.estimatedReturnToServiceDate ? 'board-return-error' : undefined}
              />
              {fieldErrors.estimatedReturnToServiceDate && (
                <div id="board-return-error" className="tw-mt-1 tw-text-sm tw-text-danger">
                  {fieldErrors.estimatedReturnToServiceDate}
                </div>
              )}
            </>
          ) : (
            <p className="tw-mb-0 tw-text-sm">Return this unit to service?</p>
          )}
          {(fieldErrors._form || fieldErrors.availability) && (
            <div className="tw-mt-3 tw-text-sm tw-text-danger" role="alert">{fieldErrors._form || fieldErrors.availability}</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelected(null)} disabled={saving}>Cancel</Button>
          <Button disabled={saving || !selected} onClick={confirm}>
            {saving ? 'Saving…' : target === 'OutOfService' ? 'Take Out of Service' : 'Return to Service'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EquipmentShopBoardPage;
